import { FC, useMemo, useState } from 'react';

import JsonSchemaSelect, { Option, OptionFilters } from 'dashboard/components/JsonSchemaSelect';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';

interface FieldSchemaSelectProps {
  initialValue: string;
  usableObjects: UsableObjectConfig[];
  ariaLabel?: string;
  ariaLabelledBy?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  id?: string;
  size?: 'sm' | 'md';
  onFieldSelect: (option: Option) => void;
}

/**
 * This component is just a simple wrapper around JsonSchemaSelect that manages the json schema generation
 * & transformation of values for you. If you don't need to access the json schema in your consuming component,
 * use this. Otherwise use JsonSchemaSelect directly.
 *
 * In the context of this component, "criteria variable" refers to the criteria defined by Persona,
 * while "Field" refers to a general field in the entire schema. Criteria variables are a subcategory
 * of fields. An example of a field that's not a criteria variable is an API trigger params field.
 *
 * @param {WorkflowCriteriaVariableSchemas=} criteriaVariableSchemas - known criteria variables defined by persona
 * @param {string[]=} allowedFieldPaths - allowlist of fields within schema
 */
const FieldSchemaSelect: FC<FieldSchemaSelectProps & OptionFilters> = ({
  ariaLabel,
  ariaLabelledBy,
  autoFocus,
  id,
  initialValue,
  size,
  usableObjects,
  allowedArrayTypes = [],
  allowedFieldPaths = [],
  allowedTypes = [],
  disabled = false,
  disallowedFieldPaths = [],
  hasError = false,
  onFieldSelect,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const [inputValue, setInputValue] = useState<string | string[]>(initialValue);

  return (
    <JsonSchemaSelect
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      hasError={hasError}
      id={id}
      inputValue={inputValue}
      jsonSchema={jsonSchema}
      autoFocus={autoFocus}
      allowedArrayTypes={allowedArrayTypes}
      allowedTypes={allowedTypes}
      allowedFieldPaths={allowedFieldPaths}
      disabled={disabled}
      disallowedFieldPaths={disallowedFieldPaths}
      tokensToDisplayValue={tokensToDisplayValue}
      onValueChange={setInputValue}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onFieldSelect={onFieldSelect}
      size={size}
    />
  );
};

export default FieldSchemaSelect;
